import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { Form, Formik, FormikHelpers } from 'formik';
import { useCountdown, useMutateOptions } from 'hooks';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  PiCaretLeft,
  PiEnvelopeSimple,
  PiPaperPlaneTilt,
  PiTimer,
} from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResendConfirmEmail } from 'services/auth/confirmEmail';
import { IResendConfirmEmailForm } from 'services/auth/types';
import { object, string } from 'yup';

const resendConfirmEmailValidationSchema = object().shape({
  email: string()
    .email(i18next.t('formik-error.invalid-email'))
    .required(i18next.t('formik-error.required')),
});

const ResendConfirmEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { countdown, isCountingDown, startCounter } = useCountdown(90, false);
  const mutateOptions = useMutateOptions<IResendConfirmEmailForm>();

  const resendConfirmEmail = useResendConfirmEmail();

  const email = params.get('email') ?? '';

  const goToLogin = () => navigate(`/login?email=${email}`);

  const onResendConfirmEmail = async (
    values: IResendConfirmEmailForm,
    helpers: FormikHelpers<IResendConfirmEmailForm>
  ) => {
    await resendConfirmEmail.mutateAsync(values, {
      ...mutateOptions(helpers),
      onSuccess: startCounter,
    });
  };

  return (
    <PublicPage
      header={
        <>
          <PiEnvelopeSimple />
          {t('resend-confirm-email.header')}
        </>
      }
      footer={
        <div
          className='flex gap-2 cursor-pointer p-4 items-center'
          onClick={goToLogin}>
          <PiCaretLeft />
          {t('resend-confirm-email.back')}
        </div>
      }>
      <div className=' w-72 opacity-65 text-center'>
        {t('resend-confirm-email.message')}
      </div>
      <Formik
        validateOnBlur
        validationSchema={resendConfirmEmailValidationSchema}
        initialValues={{ email }}
        onSubmit={onResendConfirmEmail}>
        <Form>
          <FormInput.Text
            name='email'
            type='email'
            label={t('input.label.email')}
            borderless
            appBackground
          />
          <Button
            type='submit'
            disabled={!!isCountingDown}
            isLoading={resendConfirmEmail.isPending}
            fullWidth>
            <Button.Text
              text={
                isCountingDown
                  ? t('button.resend-timer', {
                      timer: countdown,
                    })
                  : t('button.resend')
              }
            />
            <Button.Icon
              icon={isCountingDown ? <PiTimer /> : <PiPaperPlaneTilt />}
            />
          </Button>
        </Form>
      </Formik>
    </PublicPage>
  );
};

export default ResendConfirmEmail;
